import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Chip } from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";

import { DispositionSelect } from "@/pages/mill/schedule/components/mechanical-results/cells";
import { getSampleStatusLabel } from "@/pages/mill/schedule/components/mechanical-results/format";
import { Disposition } from "@/pages/mill/schedule/components/mechanical-results/types";
import { CampaignResponse } from "@/pages/mill/schedule/models/CampaignReponse";
import {
	formatDaysSinceLastBillet,
	getDaysSinceLastBillet,
	TimeSinceLastBilletCell,
} from "@/shared/components/TimeSinceLastBillet";
import { MillHeatRow } from "../repositories/LabDashboardRepository";

const SampleStatusChip = ({
	code,
}: {
	code: CampaignResponse["SampleStatusCode"] | null | undefined;
}) => {
	if (!code) return <>-</>;

	const status = getSampleStatusLabel(code);
	return (
		<Chip
			label={status.label}
			color={status.color}
			size="small"
			sx={{
				height: 18,
				fontSize: "0.65rem",
				fontWeight: 600,
				"& .MuiChip-label": { px: 0.75 },
			}}
		/>
	);
};

const SampleStatusCell = ({
	data,
	value,
	context,
}: ICellRendererParams<MillHeatRow, CampaignResponse["SampleStatusCode"]>) => {
	const {
		editingCampaignId,
		savingHeatId,
		onChangeDisposition,
	}: {
		editingCampaignId: number | null;
		savingHeatId: number | null;
		onChangeDisposition?: (heatId: number, value: Disposition) => void;
	} = context ?? {};

	const heatId = data?.HeatId;

	if (!onChangeDisposition || data?.CampaignId !== editingCampaignId) {
		return <SampleStatusChip code={value} />;
	}

	return (
		<Box
			sx={{ display: "flex", alignItems: "center", height: "100%" }}
			onClick={(event) => event.stopPropagation()}
		>
			<DispositionSelect
				value={value ?? "U"}
				disabled={savingHeatId === heatId}
				onChange={(disposition) => onChangeDisposition(heatId!, disposition)}
				sx={{
					width: "100%",
					"& .MuiSvgIcon-root": { right: 0 },
				}}
			/>
		</Box>
	);
};

export const useMillHeatsTableData = () => {
	const columnDefs: ColDef<MillHeatRow>[] = [
		{
			field: "HeatName",
			headerName: "Heat",
			flex: 1,
		},
		{
			field: "GradeName",
			headerName: "Grade",
			flex: 1,
		},
		{
			field: "SectionName",
			headerName: "Section",
			flex: 1,
		},
		{
			field: "Start",
			headerName: "Start",
			flex: 1,
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.Start, "MM/dd HH:mm") || "",
		},
		{
			field: "End",
			headerName: "End",
			flex: 1,
			valueGetter: (params) =>
				dxtToLocalServerTime(params.data?.End, "MM/dd HH:mm") || "-",
		},
		{
			colId: "TimeSinceLastBillet",
			headerName: "Since Last Billet",
			flex: 1.5,
			valueGetter: (params) => getDaysSinceLastBillet(params.data),
			valueFormatter: (params) => formatDaysSinceLastBillet(params.value),
			cellRenderer: TimeSinceLastBilletCell,
		},
		{
			field: "RolledColdTons",
			headerName: "Rolled Tons",
			flex: 1,
			valueFormatter: (params) =>
				params.value ? Number(params.value).toFixed(2) : "",
		},
		{
			field: "DelayTimePercent",
			headerName: "Delays %",
			flex: 1,
			valueFormatter: (params) =>
				params.value == null ? "" : `${Number(params.value).toFixed(0)}%`,
		},
		{
			field: "SampleStatusCode",
			headerName: "Status",
			flex: 2,
			cellRenderer: SampleStatusCell,
		},
	];

	return { columnDefs };
};
